import type { Observation } from '@medplum/fhirtypes'
import { ExtendedObservation } from '@seen/core/models/observation'
import { nodeDraft } from '@seen/core/observation-logic/noteCapture'
import { createContext, useCallback, useContext, useMemo } from 'react'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useRemoveObservationRecord } from '#src/components/rich-text-editor/ObservationNode/useRemoveObservationRecord.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

/* The one way an observation node's controls read and write its answer.
 *
 * Where the note writes its records as answers are given, the answer is a
 * record the node points at (`reference`). Where it writes them when it is
 * signed or submitted (`capture.deferred`), the answer is held in the node as
 * a draft Observation until then. Controls build and change an
 * `ExtendedObservation` the same way either way; this decides where it goes. */
export interface NodeRecord {
  // The node's answer: its draft, or the record it points at. Undefined while
  // unanswered (or while the record is read).
  observation: ExtendedObservation | undefined
  isLoading: boolean
  // The answer is a draft held in the note.
  isDraft: boolean
  // Records a first answer: held as the node's draft, or written now and
  // pointed at. Resolves to what the node now holds.
  create: (observation: ExtendedObservation) => Promise<ExtendedObservation>
  // Changes the answer in place (value, note, date). `base` is the answer to
  // change when the caller holds a newer one than `observation`, as a
  // control does between creating and re-rendering.
  patch: (
    mutateFn: (observation: ExtendedObservation) => void,
    base?: ExtendedObservation
  ) => Promise<ExtendedObservation>
  // Removes the answer: drops the draft, or removes the record (deleted while
  // preliminary, else marked entered in error) and the node's pointer to it.
  remove: () => Promise<void>
}

// The parts of an Observation a draft keeps: whom it is for, its encounter
// and its identity are the note's to give when it is signed.
export function toDraft(observation: ExtendedObservation): Observation {
  const {
    id: _id,
    meta: _meta,
    subject: _subject,
    encounter: _encounter,
    ...draft
  } = observation.toFhirResource()
  return draft
}

export function fromDraft(draft: Observation): ExtendedObservation {
  return ExtendedObservation.fromFhirResource(draft)
}

interface UseNodeRecordArgs {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  capture: ObservationCaptureTarget
  // Called once a new answer is on the node, for the host to save the note.
  onPersisted?: () => void
}

export function useNodeRecordState({
  attributes,
  updateAttributes,
  capture,
  onPersisted,
}: UseNodeRecordArgs): NodeRecord {
  const rawDraft = nodeDraft({ draft: attributes.draft })
  const draft = useMemo(
    () => (rawDraft ? fromDraft(rawDraft) : undefined),
    [rawDraft]
  )
  const { data: record, isLoading } = useRead(
    ExtendedObservation,
    draft ? undefined : (attributes.reference ?? undefined)
  )
  const { mutateAsync: createRecord } = useCreateMutation<ExtendedObservation>()
  const { mutateAsync: patchRecord } = usePatchMutation<ExtendedObservation>()
  const removeRecord = useRemoveObservationRecord()
  const observation = draft ?? record ?? undefined

  const create = useCallback(
    async (next: ExtendedObservation): Promise<ExtendedObservation> => {
      if (capture.deferred) {
        const held = toDraft(next)
        updateAttributes({ draft: held, reference: null })
        onPersisted?.()
        return fromDraft(held)
      }
      const created = await createRecord(next)
      updateAttributes({ reference: created.reference.reference ?? null })
      onPersisted?.()
      return created
    },
    [capture.deferred, createRecord, onPersisted, updateAttributes]
  )

  const patch = useCallback(
    async (
      mutateFn: (observation: ExtendedObservation) => void,
      base?: ExtendedObservation
    ): Promise<ExtendedObservation> => {
      const current = base ?? observation
      if (!current) {
        throw new Error('There is no answer to change yet')
      }
      // A draft has no id (`ExtendedObservation.id` throws without one).
      if (capture.deferred || !current.toFhirResource().id) {
        const next = fromDraft(toDraft(current))
        mutateFn(next)
        const held = toDraft(next)
        updateAttributes({ draft: held })
        return fromDraft(held)
      }
      return patchRecord({ instance: current, mutateFn })
    },
    [capture.deferred, observation, patchRecord, updateAttributes]
  )

  const remove = useCallback(async (): Promise<void> => {
    if (draft) {
      updateAttributes({ draft: null })
      return
    }
    if (record) {
      await removeRecord(record)
    }
    updateAttributes({ reference: null })
  }, [draft, record, removeRecord, updateAttributes])

  return {
    observation,
    isLoading: !draft && isLoading,
    isDraft: !!draft,
    create,
    patch,
    remove,
  }
}

export const NodeRecordContext = createContext<NodeRecord | null>(null)

// The answer of the observation node rendering this control.
export function useNodeRecord(): NodeRecord {
  const record = useContext(NodeRecordContext)
  if (!record) {
    throw new Error('useNodeRecord is only used inside an observation node')
  }
  return record
}
